/**
 * A Newick tree parser and writer.
 *
 * Newick (Felsenstein's 1986 "New Hampshire" format) writes a tree as nested
 * parentheses: `(A:0.1,B:0.2,(C:0.3,D:0.4)95:0.5);`. Each node may carry a label and a
 * branch length after a colon. This parser reads:
 *
 * - leaf and internal-node labels, unquoted or in single (or double) quotes, with `''`
 *   for a quote inside a quoted label; underscores in unquoted labels mean spaces;
 * - branch lengths, including scientific notation (`1e-3`);
 * - support values: an internal label made only of numbers, dots and slashes (`95`,
 *   `0.98`, `95/0.98`) is kept as the node's support rather than as a clade name;
 * - `[comments]`, including BEAST/NHX annotations, which are skipped;
 * - unnamed nodes (`(,,(,));`), a missing final semicolon, any whitespace or newlines,
 *   and several trees in one input (the first is returned, the rest are counted).
 */

export interface NewickNode {
  /** Tip name, or an internal node's clade name. */
  name: string | null;
  /** Branch length to the parent, as written. */
  length: number | null;
  /** Support value as written (`95`, `0.98`, `95/0.98`) on an internal node. */
  support: string | null;
  children: NewickNode[];
}

export type NewickErrorCode =
  | "empty"
  | "unclosed"
  | "unexpected"
  | "badLength"
  | "unclosedComment"
  | "unclosedQuote"
  | "tooLarge";

export interface NewickError {
  code: NewickErrorCode;
  /** The offending character or text, where there is one. */
  detail: string;
  /** Zero-based character position. */
  position: number;
}

export interface NewickResult {
  root: NewickNode | null;
  error: NewickError | null;
  /** Further trees after the first. */
  extraTrees: number;
}

const MAX_NODES = 5000;
const MAX_DEPTH = 1000;
const MAX_TREES = 1000;
/** Characters that end an unquoted label or a number. */
const STOPS = new Set(["(", ")", "[", "]", "'", '"', ",", ":", ";"]);
const WHITESPACE = /\s/;
const SUPPORT_LIKE =
  /^[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?(?:\/[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?)*$/;
const NUMBER_LIKE = /^[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/;
const PLAIN_LABEL = /^[^\s()[\]'",:;]+$/;
const UNDERSCORES = /_/g;
const SPACES = /\s+/g;

class Failure extends Error {
  readonly info: NewickError;
  constructor(info: NewickError) {
    super(info.code);
    this.info = info;
  }
}

class Reader {
  private readonly text: string;
  private pos = 0;
  nodes = 0;

  constructor(text: string) {
    this.text = text;
  }

  get position(): number {
    return this.pos;
  }

  atEnd(): boolean {
    return this.pos >= this.text.length;
  }

  take(): void {
    this.pos += 1;
  }

  peek(): string {
    return this.text[this.pos] ?? "";
  }

  fail(code: NewickErrorCode, detail = "", at = this.pos): never {
    throw new Failure({ code, detail, position: at });
  }

  /** Skip whitespace and [comments]. */
  skip(): void {
    while (!this.atEnd()) {
      const char = this.peek();
      if (WHITESPACE.test(char)) {
        this.pos += 1;
      } else if (char === "[") {
        this.skipComment();
      } else {
        return;
      }
    }
  }

  private skipComment(): void {
    const start = this.pos;
    let depth = 0;
    while (!this.atEnd()) {
      const char = this.text[this.pos];
      this.pos += 1;
      if (char === "[") {
        depth += 1;
      } else if (char === "]") {
        depth -= 1;
        if (depth === 0) {
          return;
        }
      }
    }
    this.fail("unclosedComment", "[", start);
  }

  /** A label, quoted or not; null when none starts here. */
  label(): string | null {
    const char = this.peek();
    if (char === "'" || char === '"') {
      return this.quoted(char);
    }
    const start = this.pos;
    while (
      !(this.atEnd() || STOPS.has(this.peek()) || WHITESPACE.test(this.peek()))
    ) {
      this.pos += 1;
    }
    return this.pos === start
      ? null
      : this.text.slice(start, this.pos).replace(UNDERSCORES, " ");
  }

  private quoted(mark: string): string {
    const start = this.pos;
    this.pos += 1;
    let value = "";
    while (!this.atEnd()) {
      const char = this.text[this.pos];
      this.pos += 1;
      if (char !== mark) {
        value += char;
      } else if (this.peek() === mark) {
        value += mark;
        this.pos += 1;
      } else {
        return value;
      }
    }
    return this.fail("unclosedQuote", mark, start);
  }

  /** A branch length after a colon. */
  length(): number {
    const start = this.pos;
    this.skip();
    const from = this.pos;
    while (
      !(this.atEnd() || STOPS.has(this.peek()) || WHITESPACE.test(this.peek()))
    ) {
      this.pos += 1;
    }
    const token = this.text.slice(from, this.pos);
    if (!NUMBER_LIKE.test(token)) {
      this.fail("badLength", token, from === start ? start : from);
    }
    return Number(token);
  }
}

/** The `( … )` part of an internal node. */
function readChildren(reader: Reader, node: NewickNode, depth: number): void {
  const open = reader.position;
  reader.take();
  node.children.push(subtree(reader, depth + 1));
  reader.skip();
  while (reader.peek() === ",") {
    reader.take();
    node.children.push(subtree(reader, depth + 1));
    reader.skip();
  }
  if (reader.peek() !== ")") {
    if (reader.atEnd()) {
      reader.fail("unclosed", "(", open);
    }
    reader.fail("unexpected", reader.peek());
  }
  reader.take();
  reader.skip();
}

function subtree(reader: Reader, depth: number): NewickNode {
  reader.nodes += 1;
  if (reader.nodes > MAX_NODES || depth > MAX_DEPTH) {
    reader.fail("tooLarge");
  }
  reader.skip();
  const node: NewickNode = {
    name: null,
    length: null,
    support: null,
    children: [],
  };
  if (reader.peek() === "(") {
    readChildren(reader, node, depth);
  }
  const label = reader.label();
  if (label !== null) {
    if (node.children.length > 0 && SUPPORT_LIKE.test(label)) {
      node.support = label;
    } else {
      node.name = label;
    }
  }
  reader.skip();
  if (reader.peek() === ":") {
    reader.take();
    node.length = reader.length();
  }
  return node;
}

/** Parse the first tree in `text`. */
export function parseNewick(text: string): NewickResult {
  const reader = new Reader(text);
  try {
    reader.skip();
    if (reader.atEnd()) {
      reader.fail("empty");
    }
    const root = subtree(reader, 0);
    reader.skip();
    if (reader.peek() === ";") {
      reader.take();
    } else if (!reader.atEnd()) {
      reader.fail("unexpected", reader.peek());
    }
    let extra = 0;
    reader.skip();
    while (!reader.atEnd() && extra < MAX_TREES) {
      try {
        subtree(reader, 0);
        reader.skip();
        if (reader.peek() === ";") {
          reader.take();
        }
        extra += 1;
        reader.skip();
      } catch {
        // Anything after the first tree that is not a tree is ignored.
        break;
      }
    }
    return { root, error: null, extraTrees: extra };
  } catch (error) {
    if (error instanceof Failure) {
      return { root: null, error: error.info, extraTrees: 0 };
    }
    throw error;
  }
}

/* ------------------------------------------------------------------ writing */

function writeLabel(name: string): string {
  const spaced = name.replace(SPACES, "_");
  if (PLAIN_LABEL.test(spaced) && !name.includes("_")) {
    return spaced;
  }
  return `'${name.replace(/'/g, "''")}'`;
}

/** A tree back to Newick, labels quoted where they need it, no trailing newline. */
export function toNewick(node: NewickNode): string {
  const write = (current: NewickNode): string => {
    let out = "";
    if (current.children.length > 0) {
      out += `(${current.children.map(write).join(",")})`;
    }
    if (current.name !== null) {
      out += writeLabel(current.name);
    } else if (current.support !== null) {
      out += current.support;
    }
    if (current.length !== null) {
      out += `:${current.length}`;
    }
    return out;
  };
  return `${write(node)};`;
}
